import { useState } from 'react';
import { FlexContainer } from './FlexContainer';
import { Typography } from '@mui/material';
import { Button } from '../../interaction/Buttons/Default Button/Button';
import { IconName } from '../../display/IconLibrary/IconLibraryTypes';
import { IconAvatar } from '../../display/IconAvatar/IconAvatar';
import { IconLibrary } from '../../display/IconLibrary/IconLibrary';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';

interface InfoBoxAction {
  label: string;
  iconName?: IconName;
  disabled?: boolean;
  trackingTag?: string;
  onClick: () => void;
}

interface InfoBoxProps {
  content: string;
  title?: string;
  iconName?: IconName;
  actions?: InfoBoxAction[];
}

export const InfoBox = ({
  content,
  title,
  iconName,
  actions,
}: InfoBoxProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const contentContainer = (
    <FlexContainer direction="column">
      {title && <Typography variant="titleMedium">{title}</Typography>}
      <Typography variant="bodyMedium">{content}</Typography>
    </FlexContainer>
  );

  const actionContainer = actions && (
    <FlexContainer direction="row" gap={1} alignSelf="flex-end">
      {actions.map(action => (
        <Button
          key={action.label}
          variant="text"
          onClick={action.onClick}
          disabled={action.disabled}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          startIcon={
            action.iconName ? (
              <IconLibrary
                iconName={action.iconName}
                weight="regular"
                color="secondary"
                size={20}
                useContrastColor={!isHovered}
              />
            ) : undefined
          }
          {...(action.trackingTag
            ? generateDatadogTrackingProps(action.trackingTag)
            : {})}
          sx={{
            color: 'secondary.contrastText',
            '&:hover': {
              color: 'secondary.main',
            },
          }}
        >
          <Typography variant="bodyMedium">{action.label}</Typography>
        </Button>
      ))}
    </FlexContainer>
  );

  const contentAndActionsContainer = (
    <FlexContainer direction="column" height="fit-content" flexGrow={1}>
      {contentContainer}
      {actionContainer}
    </FlexContainer>
  );

  if (iconName) {
    return (
      <FlexContainer
        direction="row"
        height="fit-content"
        gap={2}
        alignItems="flex-start"
        color="secondary.contrastText"
        backgroundColor="secondary.main"
        borderRadius="lessRounded"
        padding="16px 20px"
      >
        <FlexContainer
          direction="column"
          alignItems="center"
          justifyContent="flex-start"
          flexShrink={0}
        >
          <IconAvatar avatarType="need" />
        </FlexContainer>
        {contentAndActionsContainer}
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="row" height="fit-content" gap={2}>
      {contentAndActionsContainer}
    </FlexContainer>
  );
};
